import { usePetitionActionsPageData } from '#src/app/pages/actions/pageContext';
import type { SupporterVoicesVideoFirstExperimentVariation } from '#src/app/pages/actions/pageContext';

import { useDisableVideoCreation } from './useDisableVideoCreation';
import { useUserRecordingCapability } from './useUserRecordingCapability';

/** The arms with a video-first ask screen of their own. */
export type VideoFirstAskArm = Extract<
	SupporterVoicesVideoFirstExperimentVariation,
	'v1_emotional' | 'v2_consolidated' | 'v3_impact'
>;

/**
 * The two arms that share one ask screen, differing only in copy.
 *
 * `v2_consolidated` is excluded by type rather than by convention: it folds recording into the ask, so
 * it renders its own screen, and `VideoFirstAsk` staying a copy-only pair is what keeps v1 and v3
 * comparable.
 */
export type VideoFirstAskCopyArm = Exclude<VideoFirstAskArm, 'v2_consolidated'>;

const VIDEO_FIRST_ASK_ARMS: ReadonlySet<SupporterVoicesVideoFirstExperimentVariation> = new Set([
	'v1_emotional',
	'v2_consolidated',
	'v3_impact',
]);

/**
 * The arm whose video-first ask should render, or undefined for today's invite.
 *
 * One predicate for two decisions that must agree: which screen the arm switch picks, and whether the
 * action card is drawn around it. Disagreement gives a gradient clipped to a card, or a card missing
 * from a screen that needs one.
 */
export function useVideoFirstAskArm(): VideoFirstAskArm | undefined {
	const {
		data: { supporterVoicesVideoFirstExperiment },
	} = usePetitionActionsPageData();
	const disableVideoCreation = useDisableVideoCreation();
	const capability = useUserRecordingCapability();

	const { variation } = supporterVoicesVideoFirstExperiment;

	// Both fall back to today's invite: a petition with no video ask makes the experiment irrelevant,
	// and a viewer who cannot record would dead-end on a screen whose only path is recording.
	if (disableVideoCreation || capability !== 'capable') {
		return undefined;
	}

	// oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
	return VIDEO_FIRST_ASK_ARMS.has(variation) ? (variation as VideoFirstAskArm) : undefined;
}

/**
 * R1's arms only. R2 (CHANGE-99359) reuses the same feature's variation union, so this must be an
 * explicit allowlist rather than "not control" - R2's arms need production's ordering untouched.
 */
const R1_ARMS: ReadonlySet<SupporterVoicesVideoFirstExperimentVariation> = new Set([
	'v1_emotional',
	'v2_consolidated',
	'v3_impact',
]);

/**
 * Not "does this arm continue to the comment" but "is this arm allowed to differ from production at
 * all" - the cosmetic changes, which apply whether or not the viewer can record.
 */
export function useIsVideoFirstArm(): boolean {
	const {
		data: { supporterVoicesVideoFirstExperiment },
	} = usePetitionActionsPageData();

	return R1_ARMS.has(supporterVoicesVideoFirstExperiment.variation);
}

/**
 * Whether this viewer's arm carries on to the comment step after submitting a video.
 *
 * False for `control`, which keeps ending at the video confirmation - it is the baseline the
 * experiment measures against, so moving where its flow finishes mid-test would invalidate it.
 *
 * Every treatment arm reorders the flow this way, so the answer is currently the same as
 * `useIsVideoFirstArm`'s. Kept as its own predicate because its call sites - the confirmation redirect
 * and the way out of the recorder - ask about the reordered flow, not about cosmetics, and an arm could
 * take the new ask screen without taking the new ordering.
 */
export function useIsCommentAfterVideoArm(): boolean {
	const {
		data: { supporterVoicesVideoFirstExperiment },
	} = usePetitionActionsPageData();

	return R1_ARMS.has(supporterVoicesVideoFirstExperiment.variation);
}
